import { useState } from "react";
import { FileUp, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export const arNormalize = (s) => String(s ?? "")
  .replace(/[ً-ْٰـ]/g, "")
  .replace(/[أإآ]/g, "ا")
  .replace(/ى/g, "ي")
  .replace(/ة/g, "ه")
  .replace(/[ؤئ]/g, "ء");

export const makeGuessMapping = (headers, fields, guessMap) => {
  const taken = new Set();
  const map = {};
  for (const f of fields) {
    for (const kw of (guessMap[f.key] || [])) {
      const idx = headers.findIndex((h, i) => !taken.has(i) && arNormalize(h).includes(arNormalize(kw)));
      if (idx >= 0) { map[f.key] = String(idx); taken.add(idx); break; }
    }
  }
  return map;
};

export default function ExcelImportDialog({ open, onOpenChange, title, description, fields,
                                            guessMap, previewUrl, importUrl, onDone,
                                            withManualDate = false,
                                            testIdPrefix = "excel-import" }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [manualDate, setManualDate] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setMapping({}); setManualDate(""); };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(previewUrl, fd);
      if (!data.headers?.length) { toast.error("الملف فارغ"); return; }
      setFile(f);
      setPreview(data);
      setMapping(makeGuessMapping(data.headers, fields, guessMap));
    } catch (err) {
      toast.error(err.response?.data?.detail || "تعذر قراءة الملف");
    } finally { setBusy(false); }
  };

  const requiredOk = fields.filter((f) => f.required).every((f) => mapping[f.key])
    && (!withManualDate || !!manualDate);

  const runImport = async () => {
    if (!requiredOk) { toast.error("حدد الأعمدة الإلزامية أولاً"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      if (withManualDate) fd.append("date", manualDate);
      const { data } = await api.post(importUrl, fd);
      toast.success(`تم استيراد ${data.imported} سجل${data.skipped ? ` (تم تخطي ${data.skipped})` : ""}${data.absent_marked ? ` — وسُجّل ${data.absent_marked} غائباً تلقائياً` : ""}${data.time_fixed ? ` — وصُحّحت أوقات ${data.time_fixed} سجلاً` : ""}`);
      onOpenChange(false);
      reset();
      onDone?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الاستيراد");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" dir="rtl"
        data-testid={`${testIdPrefix}-modal`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">{description || title}</DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 leading-7">{description}</p>
            <label className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-2xl py-10 cursor-pointer hover:border-emerald-600 hover:bg-emerald-50/40 transition-colors ${busy ? "opacity-50 pointer-events-none" : ""}`}
              data-testid={`${testIdPrefix}-dropzone`}>
              <FileUp className="w-8 h-8 text-emerald-800" />
              <span className="text-sm font-semibold">{busy ? "جارٍ قراءة الملف..." : "اختر ملف Excel (.xlsx)"}</span>
              <span className="text-[11px] text-slate-400">الصف الأول يُعتبر صف العناوين</span>
              <input type="file" accept=".xlsx,.xls" className="hidden"
                data-testid={`${testIdPrefix}-file-input`} onChange={onPickFile} />
            </label>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-slate-50 border rounded-xl px-4 py-2.5">
              <p className="text-sm"><b>{file?.name}</b> — {preview.total_rows} صف</p>
              <label className="text-xs text-emerald-800 hover:underline cursor-pointer flex items-center gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> اختيار ملف آخر
                <input type="file" accept=".xlsx,.xls" className="hidden"
                  data-testid={`${testIdPrefix}-repick`} onChange={onPickFile} />
              </label>
            </div>

            <div className="overflow-x-auto border rounded-xl">
              <table className="w-full text-xs" data-testid={`${testIdPrefix}-preview-table`}>
                <thead>
                  <tr className="bg-emerald-900 text-white">
                    {preview.headers.map((h, i) => (
                      <th key={i} className="px-3 py-2 font-semibold text-center whitespace-nowrap">
                        عمود {i + 1}<br /><span className="font-normal opacity-80">{h}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.samples.map((r, ri) => (
                    <tr key={ri} className="border-t">
                      {preview.headers.map((_, ci) => (
                        <td key={ci} className="px-3 py-1.5 text-center text-slate-600 whitespace-nowrap">{r[ci] || "—"}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {withManualDate && (
              <div className="flex items-center gap-3 bg-emerald-50/60 border border-emerald-200 rounded-xl px-4 py-3">
                <span className="text-xs font-semibold shrink-0">تاريخ الدوام <span className="text-rose-600">*</span></span>
                <DateInput type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)}
                  data-testid={`${testIdPrefix}-manual-date`}
                  className="h-9 text-xs max-w-[180px]" dir="ltr" />
                <span className="text-[11px] text-slate-400">يُطبَّق على جميع صفوف الملف</span>
              </div>
            )}

            <div className="space-y-2">
              <p className="text-sm font-bold">حدد العمود المقابل لكل حقل:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`${testIdPrefix}-mapping-grid`}>
                {fields.map((f) => (
                  <div key={f.key} className="flex items-center gap-2">
                    <span className="text-xs w-32 shrink-0 font-semibold">
                      {f.label} {f.required && <span className="text-rose-600">*</span>}
                    </span>
                    <Select value={mapping[f.key] ?? "none"}
                      onValueChange={(v) => setMapping({ ...mapping, [f.key]: v === "none" ? undefined : v })}>
                      <SelectTrigger data-testid={`${testIdPrefix}-map-${f.key}`} className="h-9 text-xs flex-1">
                        <SelectValue placeholder="— لا يوجد —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— لا يوجد —</SelectItem>
                        {preview.headers.map((h, i) => (
                          <SelectItem key={i} value={String(i)}>
                            عمود {i + 1}: {h} {preview.samples[0]?.[i] ? `(${preview.samples[0][i]})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={runImport} disabled={busy || !requiredOk}
              data-testid={`${testIdPrefix}-confirm`}
              className="w-full bg-emerald-900 hover:bg-emerald-950 text-white h-11">
              {busy ? "جارٍ الاستيراد..." : `استيراد ${preview.total_rows} صف`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
